import React, { useState, useEffect, useMemo } from 'react';
import { submitComplaint } from '../APIServices/ComplaintServices';
import TranslatedText from '../components/TranslatedText';
import OTPVerification from '../components/OTPVerification';
import VoiceRecorder from '../components/VoiceRecorder';
import { useAuth } from '../contexts/AuthContext';
import encryptData from '../config/encryptConfig';
import { getApiBaseUrl } from '../config/apiConfig';
import {
  User,
  Smartphone,
  Home,
  MapPin,
  FileText,
  Image,
  Building,
  Mail,
  ChevronDown,
  Loader,
  Copy,
  CheckCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  fetchTalukas,
  fetchVillages,
} from '../APIServices/LocationService/LocationService';
import { fetchDepartments } from '../APIServices/DepartmentsServices';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services';

function NewComplaintPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  // Helper function to get department name based on current language
  const getDepartmentName = (department) => {
    return i18n.language === 'mr' ? department.nameMarathi : department.name;
  };

  // Return village name based on language
  const getVillageName = (village) => {
    if (!village) return '';
    return i18n.language === 'mr' ? village.village_name_marathi : village.name;
  };

  // Return taluka name based on language
  const getTalukaName = (taluka) => {
    if (!taluka) return '';
    return i18n.language === 'mr' ? taluka.taluka_name_marathi : taluka.name;
  };
  // Helper function to get department description based on current language
  const getDepartmentDescription = (department) => {
    const description =
      i18n.language === 'mr'
        ? department.descriptionMarathi
        : department.description;
    return description || department.code; // Fallback to code if no description
  };
  // Helper function to get taluka description based on current language
  const getTalukaDescription = (taluka) => {
    const description =
      i18n.language === 'mr' ? taluka.descriptionMarathi : taluka.description;
    return description || taluka.code;
  };

  // ✅ Age calculate  function (DOB)
  const calculateAge = (dobValue) => {
    if (!dobValue) return '';
    const today = new Date();
    const birthDate = new Date(dobValue);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  // ✅ DOB calculate function (Age)
  const calculateDobFromAge = (ageValue) => {
    if (!ageValue) return '';
    const today = new Date();
    const birthYear = today.getFullYear() - ageValue;
    const birthDate = new Date(birthYear, today.getMonth(), today.getDate());
    return birthDate.toISOString().split('T')[0];
  };

  // Effect for highlighting steps
  React.useEffect(() => {
    const interval = setInterval(() => {
      setHighlightedStep((prev) => (prev + 1) % 4);
    }, 1200);
    return () => clearInterval(interval);
  }, []);

  const [highlightedStep, setHighlightedStep] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredVillages, setFilteredVillages] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    gender: '',
    address: '',
    taluka: '',
    village: '',
    department: '',
    description: '',
    email: '',
    photos: [],
  });

  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [complaintId, setComplaintId] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [talukaInput, setTalukaInput] = useState('');
  const [filteredTalukas, setFilteredTalukas] = useState([]);
  const [showTalukaDropdown, setShowTalukaDropdown] = useState(false);
  const [talukaHighlightedIndex, setTalukaHighlightedIndex] = useState(-1);
  // ✅ Add a new state for file upload error
  const [fileError, setFileError] = useState('');
  // ✅ State to track if photo is required
  const [photoRequired, setPhotoRequired] = useState(true);
  const [photoValidationError, setPhotoValidationError] = useState('');
  const [isGovernmentEmployee, setIsGovernmentEmployee] = useState(false);
  const [govValidationError, setGovValidationError] = useState('');
  const [showGovConfirmModal, setShowGovConfirmModal] = useState(false);
  const [villageHighlightedIndex, setVillageHighlightedIndex] = useState(-1);
  // ✅ OTP Verification states
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [isOTPVerified, setIsOTPVerified] = useState(false);
  const [pendingComplaintData, setPendingComplaintData] = useState(null);

  const handleVoiceInput = (speechText) => {
    setFormData((prev) => {
      const existing = prev.description ? prev.description.trim() + ' ' : '';
      const updated = (existing + speechText).slice(0, 2000);
      return {
        ...prev,
        description: updated,
      };
    });
  };

  const [departmentInput, setDepartmentInput] = useState('');
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [departmentHighlightedIndex, setDepartmentHighlightedIndex] =
    useState(-1);
  const [emailError, setEmailError] = useState('');

  // Taluka + Village state
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [selectedTaluka, setSelectedTaluka] = useState('');

  // Departments state
  const [departments, setDepartments] = useState([]);

  // Taluka to SDO mapping based on the subdivision structure from the image
  // Maps taluka name to array of [English SDO pattern, Marathi SDO pattern]
  const talukaToSDOMapping = {
    // Gadchiroli subdivision (Sub-Division 1) -> SDO Gadchiroli
    Gadchiroli: ['SDO Gadchiroli', 'उपविभागीय अधिकारी गडचिरोली'],
    गडचिरोली: ['SDO Gadchiroli', 'उपविभागीय अधिकारी गडचिरोली'],
    Dhanora: ['SDO Gadchiroli', 'उपविभागीय अधिकारी गडचिरोली'],
    धनोरा: ['SDO Gadchiroli', 'उपविभागीय अधिकारी गडचिरोली'],

    // Chamorshi subdivision (Sub-Division 2) -> SDO Chamorshi
    Chamorshi: ['SDO Chamorshi', 'उपविभागीय अधिकारी चामोर्शी'],
    चामोर्शी: ['SDO Chamorshi', 'उपविभागीय अधिकारी चामोर्शी'],
    Mulchera: ['SDO Chamorshi', 'उपविभागीय अधिकारी चामोर्शी'],
    मुलचेरा: ['SDO Chamorshi', 'उपविभागीय अधिकारी चामोर्शी'],

    // Desaiganj (Wadsa) subdivision (Sub-Division 3) -> SDO Desaiganj (Wadsa)
    Desaiganj: ['SDO Desaiganj', 'उपविभागीय अधिकारी देसाईगंज'],
    देसाईगंज: ['SDO Desaiganj', 'उपविभागीय अधिकारी देसाईगंज'],
    Wadsa: ['SDO Desaiganj', 'उपविभागीय अधिकारी देसाईगंज'],
    वडसा: ['SDO Desaiganj', 'उपविभागीय अधिकारी देसाईगंज'],
    Armori: ['SDO Desaiganj', 'उपविभागीय अधिकारी देसाईगंज'],
    आर्मोरी: ['SDO Desaiganj', 'उपविभागीय अधिकारी देसाईगंज'],

    // Kurkheda subdivision (Sub-Division 4) -> SDO Kurkheda
    Kurkheda: ['SDO Kurkheda', 'उपविभागीय अधिकारी कुरखेडा'],
    कुर्खेडा: ['SDO Kurkheda', 'उपविभागीय अधिकारी कुरखेडा'],
    Korchi: ['SDO Kurkheda', 'उपविभागीय अधिकारी कुरखेडा'],
    कोर्ची: ['SDO Kurkheda', 'उपविभागीय अधिकारी कुरखेडा'],

    // Aheri subdivision (Sub-Division 5) -> SDO Aheri
    Aheri: ['SDO Aheri', 'उपविभागीय अधिकारी अहेरी'],
    अहेरी: ['SDO Aheri', 'उपविभागीय अधिकारी अहेरी'],
    Sironcha: ['SDO Aheri', 'उपविभागीय अधिकारी अहेरी'],
    सिरोंचा: ['SDO Aheri', 'उपविभागीय अधिकारी अहेरी'],

    // Etapalli subdivision (Sub-Division 6) -> SDO Etapalli
    'Etapalli (S)': ['SDO Etapalli', 'उपविभागीय अधिकारी एटापल्ली'],
    Etapalli: ['SDO Etapalli', 'उपविभागीय अधिकारी एटापल्ली'],
    'एतापल्ली (स)': ['SDO Etapalli', 'उपविभागीय अधिकारी एटापल्ली'],
    एतापल्ली: ['SDO Etapalli', 'उपविभागीय अधिकारी एटापल्ली'],
    Bhamaragad: ['SDO Etapalli', 'उपविभागीय अधिकारी एटापल्ली'],
    Bhamragad: ['SDO Etapalli', 'उपविभागीय अधिकारी एटापल्ली'],
    भामरगड: ['SDO Etapalli', 'उपविभागीय अधिकारी एटापल्ली'],
    भामरागड: ['SDO Etapalli', 'उपविभागीय अधिकारी एटापल्ली'],
  };

  // Helper function to check if a department is an SDO for a specific area
  const isSDOForArea = (deptName, deptNameMarathi, sdoPatterns) => {
    if (!deptName || !sdoPatterns) return false;

    const lowerDept = deptName.toLowerCase();
    const lowerDeptMarathi = deptNameMarathi
      ? deptNameMarathi.toLowerCase()
      : '';

    // sdoPatterns is an array: [English pattern, Marathi pattern]
    const [englishPattern, marathiPattern] = sdoPatterns;

    // Check if English or Marathi name starts with the respective pattern
    const matchesEnglish = lowerDept.startsWith(englishPattern.toLowerCase());
    const matchesMarathi =
      marathiPattern &&
      lowerDeptMarathi.startsWith(marathiPattern.toLowerCase());

    return matchesEnglish || matchesMarathi;
  };

  // Categories
  // const categories = [
  //   { value: 'electricity', label: 'Electricity & Power Supply' },
  //   { value: 'sanitation', label: 'Sanitation & Cleanliness' },
  //   { value: 'water', label: 'Water Supply & Drainage' },
  //   { value: 'road', label: 'Roads & Infrastructure' },
  //   { value: 'healthcare', label: 'Healthcare Services' },
  //   { value: 'education', label: 'Education & Schools' },
  //   { value: 'other', label: 'Other Issues' }
  // ];

  // 🔹 Fetch talukas on mount
  useEffect(() => {
    fetchTalukas()
      .then((data) => {
        if (Array.isArray(data)) {
          setTalukas(
            data.map((t) => ({
              id: t.taluka_id,
              name: t.taluka_name, // English
              nameMarathi: t.taluka_name_marathi, // Marathi
            }))
          );
        } else {
          setTalukas([]);
        }
      })
      .catch(() => setTalukas([]));
  }, []);
  useEffect(() => {
    // Clean up previous URLs
    photoPreviews.forEach((url) => URL.revokeObjectURL(url));
    // Create new URLs
    if (formData.photos.length > 0) {
      setPhotoPreviews(
        formData.photos.map((file) => URL.createObjectURL(file))
      );
    } else {
      setPhotoPreviews([]);
    }
    // Cleanup on unmount
    return () => {
      photoPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [formData.photos]);

  // 🔹 Fetch villages when taluka changes
  useEffect(() => {
    if (selectedTaluka) {
      fetchVillages(selectedTaluka)
        .then((data) => {
          if (Array.isArray(data)) {
            setVillages(
              data.map((v) => ({
                id: v.village_id,
                name: v.village_name,
                nameMarathi: v.village_name_marathi,
              }))
            );
          } else {
            setVillages([]);
          }
        })
        .catch(() => setVillages([]));
    } else {
      setVillages([]);
    }
  }, [selectedTaluka]);

  // 🔹 Fetch departments on mount
  useEffect(() => {
    fetchDepartments()
      .then((data) => {
        if (Array.isArray(data)) {
          setDepartments(data);
        } else {
          setDepartments([]);
        }
      })
      .catch(() => setDepartments([]));
  }, []);

  // Memoized filtered departments based on selected taluka
  const availableDepartments = useMemo(() => {
    if (!selectedTaluka || departments.length === 0) {
      return departments;
    }

    const selectedTalukaObj = talukas.find((t) => t.id === selectedTaluka);
    const talukaName = selectedTalukaObj
      ? i18n.language === 'mr'
        ? selectedTalukaObj.nameMarathi
        : selectedTalukaObj.name
      : '';
    const mappedSDO = talukaToSDOMapping[talukaName];

    const filtered = departments.filter((d) => {
      // Always exclude "Collector & DM"
      if (
        d.name === 'Collector & DM' ||
        d.nameMarathi === 'कलेक्टर व जिल्हाधिकारी'
      ) {
        return false;
      }

      // If taluka = Aheri, exclude "Additional Collector Gadchiroli"
      if (
        d.name === 'Additional Collector Gadchiroli' ||
        d.nameMarathi === 'अतिरिक्त जिल्हाधिकारी गडचिरोली' ||
        d.name === 'Additional Collector Aheri' ||
        d.nameMarathi === 'अतिरिक्त जिल्हाधिकारी अहेरी'
      ) {
        return true;
      }

      const deptName = d.name || '';
      const deptNameMarathi = d.nameMarathi || '';
      const lowerDept = deptName.toLowerCase();
      const lowerDeptMarathi = deptNameMarathi.toLowerCase();

      // Filter SDO departments based on taluka mapping
      if (mappedSDO) {
        // Check if this is an SDO department
        const isSDO =
          lowerDept.includes('sdo') ||
          lowerDept.includes('sub divisional') ||
          lowerDept.includes('sub-divisional') ||
          lowerDeptMarathi.includes('उपविभागीय अधिकारी');

        if (isSDO) {
          // This is an SDO department, check if it matches the mapped area
          const matches = isSDOForArea(deptName, deptNameMarathi, mappedSDO);

          return matches;
        }
      }

      // Filter Tehsildar departments - only show the one for the selected taluka
      const isTehsildar =
        lowerDept.includes('tehsildar') ||
        lowerDept.includes('tahsildar') ||
        lowerDeptMarathi.includes('तहसीलदार');

      if (isTehsildar) {
        // Normalize taluka name for matching (handle spelling variations and suffixes)
        let normalizedTalukaName = talukaName
          .toLowerCase()
          .replace(/\s*\(s\)\s*/g, '')
          .trim();

        // Handle spelling variations between API and department names
        // "Etapalli (S)" → "etapalli" matches "Tehsildar Etapalli"
        if (
          normalizedTalukaName === 'etapalli' ||
          normalizedTalukaName === 'एतापल्ली'
        ) {
          const matchesEtapalli =
            lowerDept.includes('etapalli') ||
            lowerDept.includes('ettapalli') ||
            lowerDeptMarathi.includes('एटापल्ली');

          return matchesEtapalli;
        }

        // "Bhamaragad" (API) vs "Bhamragad" (department) - spelling difference!
        if (
          normalizedTalukaName === 'bhamaragad' ||
          normalizedTalukaName === 'भामरगड'
        ) {
          const matchesBhamragad =
            lowerDept.includes('bhamragad') ||
            lowerDept.includes('bhamaragad') ||
            lowerDeptMarathi.includes('भामरागड') ||
            lowerDeptMarathi.includes('भामरगड');

          return matchesBhamragad;
        }

        // For all other talukas, do regular matching
        const matchesTaluka =
          lowerDept.includes(normalizedTalukaName) ||
          lowerDeptMarathi.includes(normalizedTalukaName);
        return matchesTaluka;
      }

      return true;
    });

    return filtered;
  }, [selectedTaluka, departments, talukas, i18n.language]);

  // ✅ Clear photo validation error when photos are added
  useEffect(() => {
    if (formData.photos.length > 0) {
      setPhotoValidationError('');
    }
  }, [formData.photos]);

  // ✅ Handle file input and other form changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'photo') {
      const maxFiles = 5;
      const maxSize = 10 * 1024 * 1024; // 10MB
      const currentFiles = formData.photos || [];
      const newFiles = Array.from(files);
      const validFiles = [];
      const invalidFiles = [];
      let errorMsg = '';

      // Check total file count
      if (currentFiles.length + newFiles.length > maxFiles) {
        errorMsg = `⚠️ You can upload a maximum of ${maxFiles} files.`;
        setFileError(errorMsg);
        e.target.value = '';
        return;
      }

      // Validate file size
      newFiles.forEach((file) => {
        if (file.size <= maxSize) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        errorMsg = `⚠️ Some files exceed the 10MB limit: ${invalidFiles.join(', ')}`;
        setFileError(errorMsg);
      } else {
        setFileError(''); // clear error if all good
        setPhotoValidationError(''); // Clear validation error when files are added
      }

      if (validFiles.length > 0 && !errorMsg) {
        setFormData((prev) => ({
          ...prev,
          photos: [...prev.photos, ...validFiles],
        }));
      }

      e.target.value = '';
    } else {
      if (name === 'email') setEmailError('');
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const removePhoto = (index) => {
    setFormData((prev) => {
      const updatedPhotos = prev.photos.filter((_, i) => i !== index);
      if (updatedPhotos.length < 5) setFileError('');
      return { ...prev, photos: updatedPhotos };
    });
  };

  // Submit complaint
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    if (formData.email && (!formData.email.includes('@') || !formData.email.includes('.com'))) {
      setEmailError(
        'Please enter a valid email address.'
      );
      return;
    }

    //  Show confirmation modal for government employee
    if (isGovernmentEmployee) {
      setShowGovConfirmModal(true);
      return;
    }

    // Validate department selection
    if (!formData.department) {
      alert('Please select a department for your complaint');
      return;
    }

    // ✅ Validate photo upload is compulsory
    if (photoRequired && formData.photos.length === 0) {
      setPhotoValidationError(
        `${t('Please attahch at least one file')}`);
      return;
    } else {
      setPhotoValidationError('');
    }

    // ✅ Show OTP verification before submission (COMMENTED OUT DUE TO DLT ISSUE)
    // const department = departments.find(
    //   (d) => d.id === Number(formData.department)
    // );

    // const complaintData = {
    //   name: encryptData(formData.name.trim()),
    //   mobile: encryptData(formData.mobile.trim()),
    //   address: encryptData(formData.address.trim()),
    //   village: formData.village ? Number(formData.village) : null,
    //   taluka: formData.taluka ? Number(formData.taluka) : null,
    //   description: formData.description,
    //   gender: formData.gender,
    //   email: encryptData(formData.email.trim()) || ''.trim(),
    //   departmentId: department.id,
    //   submittedDate: new Date().toISOString(),
    // };

    // // Store complaint data and show OTP verification
    // setPendingComplaintData({
    //   complaintData,
    //   files: formData.photos,
    // });

    // // Send OTP to mobile number
    // try {
    //   const apiBaseUrl = getApiBaseUrl();
    //   // FIX: Payload key must be 'phoneNumber' to match TemplateOtpRequest backend DTO
    //   await fetch(`${apiBaseUrl}/otp/send`, {
    //     method: 'POST',
    //     headers: {
    //       'Content-Type': 'application/json',
    //     },
    //     body: JSON.stringify({ phoneNumber: formData.mobile.trim() }),
    //   });

    //   setShowOTPVerification(true);
    // } catch (error) {
    //   console.error('Error sending OTP:', error);
    //   alert('Failed to send OTP. Please try again.');
    // }

    // DLT ISSUE BYPASS: Direct submission bypassing OTP block
    await continueFormSubmit();
  };

  // ✅ Handle OTP verification success
  // FIX: Accept otp argument from OTPVerification component
  const handleOTPVerificationSuccess = async (otp) => {
    if (!pendingComplaintData) return;

    setShowOTPVerification(false);
    setIsSubmitting(true);

    try {
      // FIX: Include OTP in the complaint data
      const dataWithOtp = {
        ...pendingComplaintData.complaintData,
        otp: otp,
      };

      const response = await ApiService.postMultipart(
        '/complaints',
        dataWithOtp,
        pendingComplaintData.files
      );
      setComplaintId(response.complaintId || '');
      setIsSubmitted(true);
      setPendingComplaintData(null);
    } catch (error) {
      console.error('Failed to submit complaint:', error);
      alert('Failed to submit complaint. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  //  Handle OTP verification close
  const handleOTPClose = () => {
    setShowOTPVerification(false);
    setPendingComplaintData(null);
  };

  // Handle government employee confirmation
  const handleGovConfirm = async () => {
    setShowGovConfirmModal(false);

    try {
      setGovValidationError('');
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(`${apiBaseUrl}/complaints/validate-government`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isGovernmentEmployee: true }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.allowed === false) {
        setGovValidationError(
          data.message ||
          'Government employees cannot raise complaints via this portal.'
        );
        return;
      }

      // If allowed, continue with form submission
      await continueFormSubmit();
    } catch (err) {
      console.error('Government validation failed', err);
      setGovValidationError('Validation failed. Please try again.');
    }
  };

  // Continue with actual complaint submission after validation
  const continueFormSubmit = async () => {
    // Validate email
    if (formData.email && (!formData.email.includes('@') || !formData.email.includes('.com'))) {
      setEmailError(
        'Please enter a valid email address. '
      );
      return;
    }

    // Validate department selection
    if (!formData.department) {
      alert('Please select a department for your complaint');
      return;
    }

    // ✅ Validate photo upload is compulsory
    if (photoRequired && formData.photos.length === 0) {
      setPhotoValidationError(
        `${t('Please attahch at least one file')}`
      );
      return;
    } else {
      setPhotoValidationError('');
    }

    const department = departments.find(
      (d) => d.id === Number(formData.department)
    );

    setIsSubmitting(true); // Start loading

    try {
      const complaintData = {
        name: encryptData(formData.name.trim()),
        mobile: encryptData(formData.mobile.trim()),
        address: encryptData(formData.address.trim()),
        village: formData.village ? Number(formData.village) : null,
        taluka: formData.taluka ? Number(formData.taluka) : null,
        description: formData.description,
        gender: formData.gender,
        email: encryptData(formData.email.trim()) || ''.trim(),
        departmentId: department.id,
        submittedDate: new Date().toISOString(), // Add exact client-side timestamp
      };

      // photo file (if user uploaded)
      const file = formData.photos || null;

      // call the service
      const response = await ApiService.postMultipart(
        '/complaints',
        complaintData,
        file
      );
      setComplaintId(response.complaintId || '');
      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to submit complaint:', error);
      alert('Failed to submit complaint. Please try again later.');
    } finally {
      setIsSubmitting(false); // Stop loading
    }
  };

  const handleGovCancel = () => {
    setShowGovConfirmModal(false);
  };

  const handleTrackComplaint = async () => {
    try {
      // Pass the data to the tracking page via state
      navigate('/complaint/status', {
        state: { complaintIdGenerated: complaintId },
      });
    } catch (error) {
      alert('Complaint not found!');
    }
  };
  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      mobile: '',
      gender: '',
      address: '',
      taluka: '',
      village: '',
      department: '',
      description: '',
      email: '',
      photos: [],
      villageInput: '',
    });
    setTalukaInput('');
    setDepartmentInput('');
    setSelectedTaluka('');
    setFilteredTalukas([]);
    setFilteredDepartments([]);
    setIsSubmitted(false);
    setComplaintId('');
    setCopySuccess(false);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen px-4 py-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 sm:py-8">
        <div className="w-full max-w-lg mx-auto mt-4 sm:mt-8 sm:max-w-2xl lg:max-w-3xl">
          <div
            className="p-6 text-center border shadow-xl rounded-xl border-green-200/50 bg-gradient-to-br from-green-50/95 to-emerald-50/95 backdrop-blur-sm sm:rounded-2xl sm:p-8"
            style={{
              backgroundImage:
                'radial-gradient(circle at 50% 30%, rgba(34, 197, 94, 0.1) 0%, transparent 50%)',
            }}
          >
            {/* Success Icon - Mobile Optimized */}
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full shadow-lg bg-gradient-to-r from-green-500 to-green-600 sm:mb-6 sm:h-20 sm:w-20">
              <CheckCircle className="w-8 h-8 text-white sm:h-10 sm:w-10" />
            </div>

            {/* Title - Mobile Optimized */}
            <h2 className="px-2 mb-3 text-xl font-bold leading-tight text-green-800 sm:mb-4 sm:text-2xl lg:text-3xl">
              {t('complaint.success.title')}
            </h2>

            {/* ID Label - Mobile Optimized */}
            <p className="mb-4 text-base font-medium text-green-700 sm:mb-6 sm:text-lg">
              {t('complaint.success.id')}
            </p>

            {/* Complaint ID Box with Copy Button - Mobile Optimized */}
            <div className="max-w-sm p-4 mx-auto mb-4 bg-green-100 border-2 border-green-300 rounded-lg sm:mb-6 sm:max-w-md sm:rounded-xl sm:p-6">
              <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
                <span className="text-xl font-bold text-center text-green-800 whitespace-nowrap sm:text-2xl lg:text-3xl">
                  {complaintId}
                </span>

                <button
                  onClick={() => {
                    try {
                      // ✅ First try modern API only if available
                      if (
                        typeof navigator !== 'undefined' &&
                        navigator.clipboard &&
                        typeof navigator.clipboard.writeText === 'function'
                      ) {
                        navigator.clipboard
                          .writeText(complaintId)
                          .then(() => {
                            setCopySuccess(true);
                            setTimeout(() => setCopySuccess(false), 2000);
                          })
                          .catch(() => {
                            // fallback if Clipboard API fails
                            const textArea = document.createElement('textarea');
                            textArea.value = complaintId;
                            textArea.style.position = 'fixed';
                            textArea.style.left = '-9999px';
                            document.body.appendChild(textArea);
                            textArea.focus();
                            textArea.select();
                            document.execCommand('copy');
                            document.body.removeChild(textArea);
                            setCopySuccess(true);
                            setTimeout(() => setCopySuccess(false), 2000);
                          });
                      } else {
                        // ✅ Always fallback if clipboard is undefined
                        const textArea = document.createElement('textarea');
                        textArea.value = complaintId;
                        textArea.style.position = 'fixed';
                        textArea.style.left = '-9999px';
                        document.body.appendChild(textArea);
                        textArea.focus();
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        setCopySuccess(true);
                        setTimeout(() => setCopySuccess(false), 2000);
                      }
                    } catch (err) {
                      console.error('Failed to copy:', err);
                      setCopySuccess(false);
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white transition-colors bg-green-600 rounded-lg shadow-md hover:bg-green-700 sm:px-4 sm:text-base"
                  title="Copy Complaint ID"
                >
                  <Copy className="flex-shrink-0 w-4 h-4" />
                  <span className="whitespace-nowrap">
                    {t('complaint.CopyID')}
                  </span>
                </button>
              </div>

              {copySuccess && (
                <div className="mt-3 text-sm font-medium text-green-600 animate-pulse sm:text-base">
                  {t('complaint.track')}
                </div>
              )}
            </div>

            {/* Note - Mobile Optimized */}
            <p className="px-2 mb-6 text-sm leading-relaxed text-green-600 sm:mb-8 sm:text-base">
              {t('complaint.success.note')}
            </p>

            {/* Action Buttons - hidden for admin and collector users */}
            {!(user && /^(admin|collector)$/i.test(user.role || '')) && (
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4">
                <button
                  onClick={resetForm}
                  className="w-full px-4 py-3 text-sm font-medium text-white transition-colors bg-blue-600 rounded-lg shadow-lg hover:bg-blue-700 sm:w-auto sm:px-6 sm:text-base"
                >
                  {t('complaint.RaiseNewcomplaint')}
                </button>
                <button
                  onClick={handleTrackComplaint}
                  className="w-full px-4 py-3 text-sm font-medium text-white transition-colors bg-green-600 rounded-lg shadow-lg hover:bg-green-700 sm:w-auto sm:px-6 sm:text-base"
                >
                  {t('complaint.Track Complaint')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen px-4 py-8 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-2xl font-bold text-gray-900 sm:text-3xl">
            {t('complaint.new.title')}
          </h1>
          <p className="max-w-2xl mx-auto text-lg text-gray-600">
            {t('complaint.new.subtitle')}
          </p>
        </div>

        <div className="flex flex-col items-start gap-8 md:flex-row">
          {/* Left Side - Registration Form */}
          <div className="flex-1 max-w-2xl">
            <div
              className="p-8 border shadow-2xl rounded-3xl border-blue-200/30 bg-white/90 backdrop-blur-md"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 20% 20%, rgba(184, 134, 11, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(218, 165, 32, 0.06) 0%, transparent 50%)',
              }}
            >
              <h2 className="flex items-center gap-2 mb-6 text-2xl font-bold text-gray-900">
                <FileText className="w-6 h-6" style={{ color: '#B8860B' }} />
                {t('complaint.new.complaintdetail')}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Address Information Section */}
                <div className="pt-4 border-t sm:pt-6">
                  {/* <h3 className="mb-4 text-lg font-semibold text-gray-900">{t('complaint.form.address')}</h3>  */}

                  {/* Taluka */}
                  <UniformDropdown
                    label={t('complaint.form.taluka')}
                    icon={MapPin}
                    value={talukaInput}
                    onInputChange={(val) => {
                      setTalukaInput(val);
                      const filtered = talukas.filter((t) => {
                        const talukaName =
                          i18n.language === 'mr' ? t.nameMarathi : t.name;
                        return talukaName
                          .toLowerCase()
                          .includes(val.toLowerCase());
                      });
                      setFilteredTalukas(filtered);
                    }}
                    options={filteredTalukas.length ? filteredTalukas : talukas}
                    showDropdown={showTalukaDropdown}
                    setShowDropdown={setShowTalukaDropdown}
                    highlightedIndex={talukaHighlightedIndex}
                    setHighlightedIndex={setTalukaHighlightedIndex}
                    onSelect={(t) => {
                      const displayName =
                        i18n.language === 'mr' ? t.nameMarathi : t.name;
                      setTalukaInput(displayName);
                      setFormData((prev) => ({
                        ...prev,
                        taluka: t.id,
                        village: '',
                        villageInput: '',
                      }));
                      setSelectedTaluka(t.id);
                    }}
                    placeholder={t('complaint.form.taluka')}
                    required
                    getOptionLabel={(t) =>
                      i18n.language === 'mr' ? t.nameMarathi : t.name
                    }
                    name="talukaInput"
                  />

                  {/* Village */}
                  <UniformDropdown
                    label={t('complaint.form.village')}
                    icon={MapPin}
                    value={formData.villageInput || ''}
                    onInputChange={(val) => {
                      setFormData((prev) => ({ ...prev, villageInput: val }));
                      const filtered = villages.filter((v) => {
                        const villageName =
                          i18n.language === 'mr' ? v.nameMarathi : v.name;
                        return villageName
                          .toLowerCase()
                          .includes(val.toLowerCase());
                      });
                      setFilteredVillages(filtered);
                    }}
                    options={
                      filteredVillages.length ? filteredVillages : villages
                    }
                    showDropdown={showDropdown}
                    setShowDropdown={setShowDropdown}
                    highlightedIndex={villageHighlightedIndex}
                    setHighlightedIndex={setVillageHighlightedIndex}
                    onSelect={(v) => {
                      const displayName =
                        i18n.language === 'mr' ? v.nameMarathi : v.name;
                      setFormData((prev) => ({
                        ...prev,
                        village: v.id,
                        villageInput: displayName,
                      }));
                    }}
                    placeholder={t('complaint.form.village')}
                    required
                    disabled={!selectedTaluka}
                    getOptionLabel={(v) =>
                      i18n.language === 'mr' ? v.nameMarathi : v.name
                    }
                    name="villageInput"
                  />
                </div>

                {/* Department Selection */}
                <UniformDropdown
                  label={t('complaint.form.Department')}
                  icon={Building}
                  value={departmentInput}
                  onInputChange={(val) => {
                    setDepartmentInput(val);

                    // Filter by search text
                    const filtered = availableDepartments.filter((d) => {
                      const deptName =
                        i18n.language === 'mr' ? d.nameMarathi : d.name;
                      return deptName.toLowerCase().includes(val.toLowerCase());
                    });

                    setFilteredDepartments(filtered);
                  }}
                  options={
                    filteredDepartments.length > 0
                      ? filteredDepartments
                      : availableDepartments
                  }
                  showDropdown={showDepartmentDropdown}
                  setShowDropdown={setShowDepartmentDropdown}
                  highlightedIndex={departmentHighlightedIndex}
                  setHighlightedIndex={setDepartmentHighlightedIndex}
                  onSelect={(d) => {
                    const displayName =
                      i18n.language === 'mr' ? d.nameMarathi : d.name;
                    setDepartmentInput(displayName);
                    setFormData((prev) => ({
                      ...prev,
                      department: d.id,
                    }));
                  }}
                  placeholder={t('complaint.form.Department')}
                  required
                  getOptionLabel={(d) =>
                    i18n.language === 'mr' ? d.nameMarathi : d.name
                  }
                  name="departmentInput"
                />

                {/* Complaint Details Section */}
                <div className="pt-6 border-t">
                  {/* <h3 className="mb-4 text-lg font-semibold text-gray-900">
    {t('complaint.new.complaintdetail')}
  </h3> */}
                  {/* Category */}
                  {/* <div className="mb-4">
                <label className="flex items-center block gap-2 mb-2 text-sm font-medium text-gray-700">
                  <List className="w-5 h-5 text-gray-400" />
                  {t('complaint.form.category')} *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('complaint.form.category')}</option>
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>{category.label}</option>
                  ))}
                </select>
              </div> */}

                  {/* Description */}
                  <div className="mb-3 sm:mb-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <FileText className="w-5 h-5 text-gray-400" />
                        {t('complaint.form.description')} *
                      </label>
                      <VoiceRecorder onSpeechRecognized={handleVoiceInput} />
                    </div>

                    <div className="relative">
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={(e) => {
                          const text = e.target.value;
                          // Character limit check
                          if (text.length <= 2000) {
                            handleChange(e);
                          }
                        }}
                        required
                        rows="8"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:border-transparent focus:ring-2 focus:ring-blue-500"
                        placeholder={t('complaint.form.description')}
                      />
                      {/* Character Count inside textarea box */}
                      <span
                        className={`absolute bottom-2.5 right-4 text-xs px-2 py-2 rounded bg-white/70 backdrop-blur-sm ${formData.description.length >= 2000
                          ? 'text-red-500 font-semibold'
                          : 'text-gray-500'
                          }`}
                      >
                        {formData.description.length} /2000
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-left text-gray-500 sm:text-left">
                      {t('complaint.form.providedetailed')}
                    </p>
                  </div>

                  {/* Government Employee Checkbox */}
                  <div className="mb-6">
                    <div className="flex items-start gap-3">
                      {/* <input
                        type="checkbox"
                        id="govt-employee-checkbox"
                        checked={isGovernmentEmployee}
                        onChange={(e) => {
                          setIsGovernmentEmployee(e.target.checked);
                          setGovValidationError('');
                        }}
                        className="w-4 h-4 mt-1 border-gray-300 rounded cursor-pointer"
                        style={{ accentColor: '#B8860B', boxShadow: '0 0 0 3px rgba(184, 134, 11, 0.1)' }}
                        onFocus={(e) => {
                          e.target.style.boxShadow = '0 0 0 3px rgba(184, 134, 11, 0.3)';
                        }}
                        onBlur={(e) => {
                          e.target.style.boxShadow = '0 0 0 3px rgba(184, 134, 11, 0.1)';
                        }}
                      /> */}

                      <div className="flex-1">
                        {/* <label htmlFor="govt-employee-checkbox" className="text-sm font-medium text-gray-700 cursor-pointer">
                        <TranslatedText>  I am a government employee </TranslatedText>
                        </label> */}

                        <p className="mt-1 text-xs text-gray-500">
                          <div className="text-red-700">
                            <TranslatedText>
                              Note:- If your are a government employee, please
                              do not raise your service related complaints here.
                              Raise it with your conserned department.
                            </TranslatedText>
                          </div>
                        </p>
                        {govValidationError && (
                          <p className="mt-2 text-sm font-medium text-red-600">
                            {govValidationError}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Personal Information Section */}
                  <div className="pt-4 border-t sm:pt-6">
                    <h3 className="mb-4 text-lg font-semibold text-gray-900">
                      {t('complaint.form.personalinformation')}
                    </h3>

                    {/* Name */}
                    <div className="mb-3 sm:mb-4">
                      <label className="flex items-center block gap-2 mb-2 text-sm font-medium text-gray-700">
                        <User className="w-5 h-5 text-gray-400" />
                        {t('complaint.form.name')} *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        pattern="^[A-Za-z\u0900-\u097F\s]+$"
                        onInput={(e) => {
                          const regex = /[^A-Za-z\u0900-\u097F\s]/g;
                          e.target.value = e.target.value.replace(regex, '');
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent focus:ring-2 focus:ring-blue-500"
                        placeholder={t('complaint.form.name')}
                      />
                    </div>

                    {/* Mobile, Email & Address in one row */}
                    <div className="flex flex-col gap-4 mb-4 sm:flex-row">
                      {/* Mobile */}
                      <div className="flex-1">
                        <label className="flex items-center block gap-2 mb-2 text-sm font-medium text-gray-700">
                          <Smartphone className="w-5 h-5 text-gray-400" />
                          {t('complaint.form.mobile')} *
                        </label>
                        <input
                          type="tel"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleChange}
                          required
                          pattern="[0-9]{10}"
                          maxLength={10}
                          title="Please enter a valid 10-digit mobile number"
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(
                              /[^0-9]/g,
                              ''
                            );
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent focus:ring-2 focus:ring-blue-500"
                          placeholder={t('complaint.form.mobile')}
                        />
                      </div>

                      {/* Email */}
                      <div className="flex-1">
                        <label className="flex items-center block gap-2 mb-2 text-sm font-medium text-gray-700">
                          <Mail className="w-5 h-5 text-gray-600" />
                          {t('complaint.form.email')}
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}

                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent focus:ring-2 focus:ring-blue-500"
                          placeholder={t('complaint.form.email')}
                        />
                        {emailError && (
                          <p className="mt-1 text-sm text-red-600">{emailError}</p>
                        )}
                      </div>
                    </div>
                    {/* Full Address  */}
                    <div className="mb-4">
                      <label className="flex items-center block gap-2 mb-2 text-sm font-medium text-gray-700">
                        <Home className="w-5 h-5 text-gray-400" />
                        {t('complaint.form.address')}
                      </label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={(e) => {
                          const value = e.target.value;
                          const filtered = value.replace(
                            /[^a-zA-Z0-9\u0900-\u097F\s,.\-]/g,
                            ''
                          );
                          handleChange({
                            target: { name: 'address', value: filtered },
                          });
                        }}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent focus:ring-2 focus:ring-blue-500"
                        placeholder={t('complaint.form.address')}
                      />
                    </div>

                    {/* Upload Photo - moved up */}
                    <div className="mb-4 sm:mb-6">
                      <label className="flex items-center block gap-2 mb-2 text-sm font-medium text-gray-700">
                        <Image className="w-5 h-5 text-gray-400" />
                        {t('complaint.form.photo')}
                        <span className="font-bold text-gray-600">*</span>
                      </label>

                      {/* Hidden input */}
                      <input
                        type="file"
                        id="upload-photo"
                        name="photo"
                        onChange={handleChange}
                        accept="image/*,application/pdf,.doc,.docx"
                        multiple
                        disabled={formData.photos.length >= 5}
                        className="hidden"
                      />

                      {/* Custom button */}
                      <label
                        htmlFor="upload-photo"
                        className={`inline-block rounded-lg px-4 py-2 border ${formData.photos.length >= 5
                          ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-white-100 border-gray-300 cursor-pointer hover:bg-gray-200'
                          }`}
                      >
                        {formData.photos.length >= 5
                          ? 'Maximum files reached'
                          : t('complaint.form.ChooseFiles')}
                      </label>

                      {/* Red line error message for >5 files or photo validation error */}
                      {fileError && (
                        <p className="mt-1 text-sm font-medium text-red-600">
                          {fileError}
                        </p>
                      )}
                      {photoValidationError && (
                        <p className="mt-2 text-sm font-medium text-red-600">
                          {photoValidationError}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-gray-500">
                          {t('complaint.form.note')}
                        </p>
                        <span
                          className={`text-sm font-medium ${formData.photos.length >= 5
                            ? 'text-red-600'
                            : 'text-gray-600'
                            }`}
                        >
                          {formData.photos.length}/5 files
                        </span>
                      </div>

                      {/* ✅ Preview grid with remove option */}
                      {photoPreviews.length > 0 && (
                        <div className="flex flex-wrap mt-3">
                          {formData.photos.map((file, index) => (
                            <div key={index} className="relative">
                              {file.type.startsWith('image/') ? (
                                <img
                                  src={photoPreviews[index]}
                                  alt={`preview-${index}`}
                                  className="w-24 h-16 object-cover rounded-md border m-[1px]"
                                />
                              ) : file.type === 'application/pdf' ? (
                                <div className="w-24 h-16 flex items-center justify-center border rounded-md bg-gray-50 text-xs text-gray-600 m-[1px]">
                                  📄 PDF
                                </div>
                              ) : file.type === 'application/msword' ||
                                file.type ===
                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ? (
                                <div className="w-24 h-16 flex items-center justify-center border rounded-md bg-gray-50 text-xs text-gray-600 m-[1px]">
                                  📑 DOC
                                </div>
                              ) : (
                                <div className="w-24 h-16 flex items-center justify-center border rounded-md bg-gray-50 text-xs text-gray-600 m-[1px]">
                                  📎 FILE
                                </div>
                              )}

                              {/* Remove button */}
                              <button
                                type="button"
                                onClick={() => removePhoto(index)}
                                className="absolute bottom-0 left-0 flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full shadow-md hover:bg-red-700"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Submit Button */}
                <div className="pt-4 border-t sm:pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl shadow-lg transition-all duration-300 font-medium text-base sm:text-lg ${isSubmitting
                      ? 'opacity-75 cursor-not-allowed'
                      : 'hover:shadow-xl transform hover:scale-[1.02]'
                      }`}
                    style={{
                      background:
                        'linear-gradient(135deg, #B8860B 0%, #DAA520 50%, #B8860B 100%)',
                    }}
                  >
                    <span className="flex items-center justify-center gap-2">
                      {isSubmitting ? (
                        <Loader className="w-5 h-5 animate-spin" />
                      ) : (
                        <FileText className="w-5 h-5" />
                      )}
                      {isSubmitting
                        ? 'Submitting...'
                        : t('complaint.form.submitcomplaint')}
                    </span>
                  </button>
                  <p className="mt-2 text-xs text-center text-gray-600 sm:mt-3 sm:text-sm">
                    {t('complaint.form.submitnote')}
                  </p>
                </div>
              </form>
            </div>
          </div>

          {/* Right Side - How It Works Guide */}
          {/* <div className="flex flex-col items-center w-full gap-6 md:w-80">
            <div className="p-6 border shadow-xl rounded-2xl border-emerald-200/50 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50"
                 style={{
                   backgroundImage: 'radial-gradient(circle at 30% 30%, rgba(16, 185, 129, 0.08) 0%, transparent 50%)'
                 }}>
              <h3 className="mb-6 text-lg font-bold text-center text-gray-800">{t('howItWorks.title')}</h3>

              {/* 3D Animated Steps */}
          {/* <div className="space-y-6">
  {[
    { icon: "📝", key: "submitComplaint", color: "from-yellow-600 via-yellow-700 to-yellow-800", shadowColor: "shadow-yellow-300" },
    { icon: "🎫", key: "getComplaintID", color: "from-orange-500 via-amber-500 to-yellow-500", shadowColor: "shadow-amber-300" },
    { icon: "👨‍💼", key: "officialReview", color: "from-emerald-500 via-teal-500 to-teal-600", shadowColor: "shadow-teal-300" },
    { icon: "📱", key: "trackProgress", color: "from-yellow-500 via-yellow-600 to-yellow-700", shadowColor: "shadow-yellow-300" }
  ].map((step, idx) => (
    <React.Fragment key={idx}>
      <div className={`
        relative bg-white rounded-xl p-4 border border-gray-200
        transition-all duration-700 cursor-pointer group
        hover:shadow-2xl hover:-translate-y-2 hover:scale-105
        ${highlightedStep === idx ?
          `animate-pulse shadow-2xl -translate-y-2 scale-105 ${step.shadowColor} border-opacity-50` :
          'hover:shadow-lg'
        }
      `}
        style={{
          transform: highlightedStep === idx ?
            'translateY(-8px) rotateX(5deg) rotateY(2deg) scale(1.05)' :
            'translateY(0px) rotateX(0deg) rotateY(0deg) scale(1)',
          transformStyle: 'preserve-3d',
          perspective: '1000px'
        }} */}
          {/* > */}
          {/* <div className={`
          absolute inset-0 bg-gradient-to-r ${step.color}
          rounded-xl opacity-0 group-hover:opacity-10
          transition-opacity duration-300
          ${highlightedStep === idx ? 'opacity-20' : ''}
        `}></div> */}

          {/* <div className="relative z-10 flex items-center gap-3">
          <div className={`
            w-12 h-12 rounded-full bg-gradient-to-r ${step.color}
            flex items-center justify-center text-white font-bold text-lg
            shadow-lg group-hover:shadow-xl transition-all duration-300
            ${highlightedStep === idx ? 'animate-bounce shadow-2xl' : ''}
          `}>
            <span className="text-xl">{step.icon}</span>
          </div>
          <div className="flex-1">
            <h4 className={`
              font-semibold text-gray-800 group-hover:text-gray-900
              transition-colors duration-300
              ${highlightedStep === idx ? 'text-blue-700' : ''}
            `}>
              {t(`howItWorks.steps.${step.key}.title`)}
                      </h4>
                       <p className="text-sm text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
              {t(`howItWorks.steps.${step.key}.description`)}
            </p>
                    </div>
                  </div> */}

          {/* Step Number */}
          {/* <div className={`
                    absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r ${step.color}
                    rounded-full flex items-center justify-center text-white text-xs font-bold
                    shadow-lg group-hover:shadow-xl transition-all duration-300
                    ${highlightedStep === idx ? 'animate-spin shadow-2xl scale-125' : ''}
                  `}>
                    {idx + 1}
                  </div>
                </div> */}

          {/* 3D Arrow Connector */}
          {/* {idx < 3 && (
                  <div className="flex justify-center"> */}
          {/* <div className={`
                      text-3xl transition-all duration-500 transform
                      ${highlightedStep === idx ? 'animate-bounce text-blue-600 scale-125' : 'text-gray-400'}
                    `}>
                      ⬇️
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div> */}

          {/* Bottom Note */}
          {/* <div className="p-3 mt-6 border border-green-200 rounded-lg bg-green-50">
                <p className="text-sm font-medium text-center text-green-700">
                   {t('howItWorks.bottomNote')}
                </p>
              </div>
            </div>
          </div> */}
        </div>
      </div>

      {/* Government Employee Confirmation Modal */}
      {showGovConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black bg-opacity-50">
          <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-2xl">
            <h2 className="mb-4 text-lg font-bold" style={{ color: '#B8860B' }}>
              Confirm Employment Status
            </h2>
            <p className="mb-6 text-sm text-gray-700">
              Are you sure you are not a government employee?
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleGovCancel}
                className="flex-1 px-4 py-2 font-medium transition-colors border-2 rounded-lg hover:opacity-80"
                style={{
                  borderColor: '#B8860B',
                  color: '#B8860B',
                  backgroundColor: '#FFF8F0',
                }}
              >
                No, I'm an Employee
              </button>
              <button
                onClick={handleGovConfirm}
                className="flex-1 px-4 py-2 font-medium text-white transition-colors rounded-lg hover:shadow-lg"
                style={{
                  background:
                    'linear-gradient(135deg, #B8860B 0%, #DAA520 100%)',
                }}
              >
                Yes, Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ OTP Verification Modal */}
      {showOTPVerification && (
        <OTPVerification
          mobile={formData.mobile}
          onVerificationSuccess={handleOTPVerificationSuccess}
          onBack={handleOTPClose}
          skipApiVerification={true}
        />
      )}
    </div>
  );
}

function handleAutocompleteKeyDown({
  event,
  showDropdown,
  filteredList,
  highlightedIndex,
  setHighlightedIndex,
  onSelect,
  setShowDropdown, // <-- Add this line
}) {
  if (!showDropdown || filteredList.length === 0) return;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    setHighlightedIndex((prev) => (prev + 1) % filteredList.length);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    setHighlightedIndex(
      (prev) => (prev - 1 + filteredList.length) % filteredList.length
    );
  } else if (event.key === 'Enter') {
    event.preventDefault();
    if (highlightedIndex >= 0 && highlightedIndex < filteredList.length) {
      onSelect(filteredList[highlightedIndex]);
    }
  } else if (event.key === 'Escape') {
    setShowDropdown(false); // <-- Now this is always defined
  }
}
// ...existing code...

function UniformDropdown({
  label,
  icon: Icon,
  value,
  onInputChange,
  options,
  showDropdown,
  setShowDropdown,
  highlightedIndex,
  setHighlightedIndex,
  onSelect,
  placeholder,
  required = false,
  disabled = false,
  getOptionLabel,
  name,
}) {
  return (
    <div className="relative mb-4">
      <label className="flex items-center block gap-2 mb-2 text-sm font-medium text-gray-700">
        <Icon className="w-5 h-5 text-gray-400" />
        {label} {required && '*'}
      </label>
      <div className="relative">
        <input
          type="text"
          name={name}
          value={value}
          onChange={(e) => {
            onInputChange(e.target.value);
            setShowDropdown(true);
          }}
          placeholder={placeholder}
          className="w-full p-2 pr-10 border rounded"
          autoComplete="off"
          required={required}
          disabled={disabled}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 100)}
          onKeyDown={(e) =>
            handleAutocompleteKeyDown({
              event: e,
              showDropdown,
              filteredList: options,
              highlightedIndex,
              setHighlightedIndex,
              onSelect,
              setShowDropdown, // <-- Pass this prop
            })
          }
        />
        <ChevronDown
          className="absolute text-gray-500 transform -translate-y-1/2 pointer-events-none right-3 top-1/2"
          size={20}
        />
      </div>
      {showDropdown && options.length > 0 && (
        <ul className="absolute z-10 w-full mt-1 bg-white border rounded shadow-md dropdown-scroll max-h-72">
          {options.map((opt, idx) => (
            <li
              key={opt.id || opt.value || idx}
              onMouseDown={() => {
                onSelect(opt);
                setShowDropdown(false);
              }}
              className={`p-2 hover:bg-blue-100 cursor-pointer ${highlightedIndex === idx ? 'bg-blue-100' : ''
                }`}
            >
              {getOptionLabel(opt)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default NewComplaintPage;
